<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, toRef } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import { TokenDiamondIcon } from '@uiKit/icons'
import { formatAed, formatToInternational } from '@shared/utils'
import { useTokenDraft } from '../composables/useTokenDraft'

// Asset card at the top of the invest drawer (Figma 32590:67010): asset name,
// min / max tokens, a slider and a − / count / + stepper sharing one value,
// then the order's value and the ownership share it buys. Validation rules
// live in ../utils/tokenQuantityUtils.ts.

interface Props {
  assetName: string
  minTokens: number
  /** Per-investor cap. */
  maxTokens: number
  /** Slider's upper end — the cap limited by stock, never below min. */
  sliderMax: number
  /** VALUE row, already resolved by the parent (server gross when it matches). */
  investmentValue: number
  /** "19.35%"; null hides the row. */
  ownershipShare: string | null
  /** What the OWNERSHIP SHARE ⓘ opens. */
  ownershipSheet: InfoSheetContent
}

const props = defineProps<Props>()

const tokens = defineModel<number>({ required: true })

const PERCENT_BASE = 100

const isFixed = computed(() => props.sliderMax <= props.minTokens)
const fillPct = computed(() =>
  isFixed.value
    ? PERCENT_BASE
    : (Math.min(tokens.value - props.minTokens, props.sliderMax - props.minTokens) /
        (props.sliderMax - props.minTokens)) *
      PERCENT_BASE,
)

const {
  fieldValue,
  rangeError,
  canDecrement,
  canIncrement,
  onBeforeInput,
  onInput,
  onCommit,
  setTokens,
} = useTokenDraft(tokens, toRef(props, 'minTokens'), toRef(props, 'maxTokens'))

const onSlide = (event: Event) => setTokens(Number((event.target as HTMLInputElement).value))
</script>

<template>
  <Box direction="col" gap="4" class="token-card">
    <Box align="center" justify="between" gap="3">
      <Box direction="col" gap="1" class="token-card__name">
        <Text type="body" weight="semibold" color="muted" transform="uppercase" is="span"
          >Asset Name</Text
        >
        <Text type="heading" weight="bold" is="span" truncate style="line-height: 24px">{{
          assetName
        }}</Text>
      </Box>
      <Box align="center" justify="center" class="token-card__mark" aria-hidden="true">
        <TokenDiamondIcon :size="28" />
      </Box>
    </Box>

    <Box class="token-card__rule" />

    <Box direction="col" gap="20px">
      <Box align="center" justify="between">
        <Box direction="col" gap="1">
          <Text type="body" weight="medium" color="muted" is="span">Tokens</Text>
          <Text type="body" weight="semibold" is="span"
            >Minimum: {{ formatToInternational(minTokens) }}</Text
          >
        </Box>
        <Box direction="col" align="end" gap="6px">
          <Text type="body" weight="medium" color="muted" is="span">Tokens</Text>
          <Text type="body" weight="semibold" is="span"
            >Maximum: {{ formatToInternational(maxTokens) }}</Text
          >
        </Box>
      </Box>

      <Box class="token-card__slider" :style="{ '--fill': `${fillPct}%` }">
        <!-- Raw <input type="range"> — no kit slider element yet (§19 exception). -->
        <input
          type="range"
          class="token-card__range"
          :min="minTokens"
          :max="sliderMax"
          step="1"
          :value="tokens"
          :disabled="isFixed"
          aria-label="Number of tokens"
          @input="onSlide"
        />
      </Box>

      <Box align="center" justify="between" gap="3">
        <Box
          is="button"
          type="button"
          align="center"
          justify="center"
          class="token-card__step"
          :disabled="!canDecrement"
          aria-label="One token fewer"
          @click="setTokens(tokens - 1)"
        >
          <PiIcon name="minus" class="token-card__step-icon" />
        </Box>
        <Box
          align="center"
          justify="center"
          class="token-card__count"
          :class="{ 'token-card__count--invalid': rangeError }"
        >
          <!-- Raw <input> — inline numeric stepper field (§19 exception). -->
          <input
            :value="fieldValue"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="off"
            class="token-card__count-input"
            aria-label="Tokens to buy"
            :aria-invalid="Boolean(rangeError)"
            aria-describedby="token-range-error"
            @beforeinput="onBeforeInput"
            @input="onInput"
            @blur="onCommit"
            @keydown.enter.prevent="onCommit"
          />
        </Box>
        <Box
          is="button"
          type="button"
          align="center"
          justify="center"
          class="token-card__step"
          :disabled="!canIncrement"
          aria-label="One token more"
          @click="setTokens(tokens + 1)"
        >
          <PiIcon name="plus" class="token-card__step-icon" />
        </Box>
      </Box>
      <Box
        v-if="rangeError"
        id="token-range-error"
        align="center"
        justify="center"
        gap="1"
        role="alert"
      >
        <PiIcon name="exclamation-circle" class="token-card__error-icon" />
        <Text type="caption" color="danger" is="span"><CurrencyText :text="rangeError" /></Text>
      </Box>
    </Box>

    <Box direction="col" gap="10px">
      <Box align="center" justify="between" class="token-card__line">
        <Text type="body" weight="medium" color="var(--p-surface-600)" is="span">VALUE</Text>
        <Text type="heading" weight="bold" is="span"
          ><CurrencyText :text="formatAed(investmentValue)"
        /></Text>
      </Box>
      <Box v-if="ownershipShare" align="center" justify="between">
        <Box align="center" gap="2">
          <Text type="body" weight="medium" color="var(--p-surface-600)" is="span"
            >OWNERSHIP SHARE</Text
          >
          <InfoSheetButton :content="ownershipSheet" label="About ownership share" />
        </Box>
        <Text type="heading" weight="bold" is="span">{{ ownershipShare }}</Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.token-card {
  padding: 22px;
  border: 1px solid var(--p-surface-200);
  border-radius: 20px;
  background: var(--p-surface-0);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);
}

.token-card__name {
  min-width: 0;
}

.token-card__mark {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid var(--p-primary-200);
  border-radius: 100px;
  background: var(--p-primary-50);
}

.token-card__rule {
  height: 1px;
  background: var(--p-surface-100);
}

.token-card__slider {
  position: relative;
  height: 10px;
}

/* Track: grey, with the chosen share filled in brand violet via --fill. */
.token-card__range {
  width: 100%;
  height: 10px;
  margin: 0;
  border-radius: 200px;
  background: linear-gradient(
    to right,
    var(--p-primary-600) 0 var(--fill),
    var(--p-surface-200) var(--fill) 100%
  );
  appearance: none;
  cursor: pointer;
}

.token-card__range:disabled {
  cursor: default;
}

.token-card__range::-webkit-slider-thumb {
  width: 32px;
  height: 32px;
  border: 4px solid var(--p-primary-600);
  border-radius: 50%;
  background: var(--p-surface-0);
  box-shadow:
    0 12px 16px rgba(10, 13, 18, 0.08),
    0 4px 6px rgba(10, 13, 18, 0.03);
  appearance: none;
}

.token-card__range::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border: 4px solid var(--p-primary-600);
  border-radius: 50%;
  background: var(--p-surface-0);
  box-shadow: 0 4px 6px rgba(10, 13, 18, 0.08);
}

.token-card__range:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 6px;
}

.token-card__step {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid var(--p-surface-200);
  border-radius: 200px;
  background: var(--p-surface-0);
  cursor: pointer;
}

.token-card__step:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.token-card__step:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.token-card__step-icon {
  font-size: 12px;
  color: var(--p-text-color);
}

.token-card__count {
  width: 211px;
  height: 36px;
  border: 1px solid var(--p-surface-300);
  border-radius: 10px;
  background: var(--p-surface-0);
}

.token-card__count--invalid,
.token-card__count--invalid:focus-within {
  border-color: var(--p-danger-500);
  background: var(--p-danger-50);
}

.token-card__count--invalid .token-card__count-input {
  color: var(--p-danger-600);
}

.token-card__error-icon {
  font-size: 12px;
  color: var(--p-danger-500);
}

.token-card__count:focus-within {
  border-color: var(--p-primary-500);
}

.token-card__count-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  font: inherit;
  font-size: 18px;
  font-weight: 600;
  color: var(--p-text-color);
}

.token-card__line {
  height: 33px;
  border-bottom: 1px solid var(--p-surface-100);
}
</style>
